/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomSelectVariantColor.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Popover,
  Grid,
  Typography,
  IconButton,
  InputAdornment,
  Divider,
} from "@mui/material";
import { ChromePicker } from "react-color";
import CustomButtonV2 from "../button/CustomButtonV2";
import InfoButton from "../../../infoButton/InfoButton";

const STANDARD_COLORS = [
  "#FFFFFF",
  "#000000",
  "#C0C0C0",
  "#808080",
  "#FF0000",
  "#00FF00",
  "#0000FF",
  "#FFFF00",
  "#00FFFF",
  "#FF00FF",
  "#FFA500",
  "#FF4500",
  "#FFD700",
  "#FF6347",
  "#FF7F50",
  "#FF69B4",
  "#FFC0CB",
  "#DC143C",
  "#B22222",
  "#8B0000",
  "#1E90FF",
  "#4169E1",
  "#6495ED",
  "#00BFFF",
  "#20B2AA",
  "#008080",
  "#008B8B",
  "#2E8B57",
  "#006400",
  "#32CD32",
  "#A52A2A",
  "#8B4513",
  "#D2691E",
  "#CD853F",
  "#F4A460",
  "#DEB887",
  "#D2B48C",
  "#BC8F8F",
  "#E6E6FA",
  "#FFF0F5",
  "#E0FFFF",
  "#F0FFF0",
  "#FAFAD2",
  "#F5DEB3",
  "#F0E68C",
  "#FFE4E1",
  "#E9967A",
  "#2196F3",
  "#4CAF50",
  "#FF9800",
  "#9C27B0",
  "#673AB7",
  "#3F51B5",
  "#00BCD4",
  "#009688",
  "#795548",
  "#607D8B",
];

export default function CustomSelectVariantColor({
  value,
  setValue,
  label,
  required,
  txtFldStyle,
  infoname,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [recentColors, setRecentColors] = useState([]);
  const [tempColor, setTempColor] = useState(value || "");

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("recentColors") || "[]");
    setRecentColors(saved);
  }, []);

  useEffect(() => {
    localStorage.setItem("recentColors", JSON.stringify(recentColors));
  }, [recentColors]);

  const handleOpenPopover = (event) => {
    setTempColor(value || "");
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleTempSelect = (color) => setTempColor(color);

  const handleApply = () => {
    setValue(tempColor);
    setRecentColors((prev) =>
      [tempColor, ...prev.filter((c) => c !== tempColor)].slice(0, 8),
    );
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? "color-popover" : undefined;

  const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#d9d9d9", borderRadius: "0px" },
      "&:hover fieldset": { borderColor: "#8c8c8c" },
      "&.Mui-focused fieldset": { borderColor: "#000000" },
    },
    "& .MuiInputLabel-root": {
      color: "#000",
      fontSize: "0.9rem",
      "&.Mui-error": { color: "#e53935" },
      "&.Mui-disabled": { color: "#bdbdbd" },
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#999",
      opacity: 1,
    },
    ...txtFldStyle,
  };

  return (
    <Box>
      <Box sx={{ display: "flex" }}>
        {label && (
          <Typography
            sx={{
              mb: 0.5,
              color: "#6f7174",
              fontSize: "0.9rem",
              fontWeight: 500,
            }}
          >
            {label}
            {required && <span style={{ color: "red" }}> *</span>}
          </Typography>
        )}
        <Box sx={{ width: 5, height: 5 }}>
          {infoname?.length > 0 ? (
            <InfoButton
              dimension={{ width: 15, height: 15 }}
              infoKey={infoname}
              color={"black"}
            />
          ) : null}
        </Box>
      </Box>

      <TextField
        size="small"
        fullWidth
        value={null}
        sx={textFieldStyle}
        InputProps={{
          startAdornment: value ? (
            <Box
              sx={{
                width: 30,
                height: 26,
                bgcolor: value,
                border: "1px solid #ccc",
                borderRadius: "0",
              }}
            />
          ) : null,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleOpenPopover}>
                <Typography size="small">{value}</Typography>
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: 2, maxWidth: 260 }}>
          <Typography variant="subtitle2">Standard Colors</Typography>

          <Divider />
          <Grid container spacing={1} sx={{ mt: 1 }}>
            {STANDARD_COLORS.map((color) => (
              <Grid item key={color}>
                <Box
                  onClick={() => handleTempSelect(color)}
                  sx={{
                    width: 30,
                    height: 30,
                    bgcolor: color,
                    border:
                      tempColor === color ? "2px solid #000" : "1px solid #ccc",
                    cursor: "pointer",
                    borderRadius: 0,
                  }}
                />
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ mt: 1 }} />
          {recentColors.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                Recent Colors
              </Typography>
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {recentColors.map((color) => (
                  <Grid item key={color}>
                    <Box
                      onClick={() => handleTempSelect(color)}
                      sx={{
                        width: 30,
                        height: 30,
                        bgcolor: color,
                        border:
                          tempColor === color
                            ? "2px solid #000"
                            : "1px solid #ccc",
                        cursor: "pointer",
                        borderRadius: 0,
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Custom Color
          </Typography>
          <ChromePicker
            color={tempColor || "#000"}
            onChangeComplete={(color) => handleTempSelect(color.hex)}
          />

          <Divider sx={{ mt: 1 }} />

          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              display: "flex",
              justifyContent: "flex-end",
              bgcolor: "white",
              zIndex: 10,

              boxShadow: "0 -2px 6px rgba(0,0,0,0.1)",
            }}
          >
            <CustomButtonV2
              btnStyle={{ marginRight: 1, marginTop: 1, marginBottom: 1 }}
              type="submit"
              btnName="Apply"
              handleOnClick={handleApply}
            />
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
